import React, { useState } from "react";
import { useFormik } from "formik";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import BaseButton from "../../../components/ui/BaseButton";
import BaseLoader from "../../../components/ui/BaseLoader";
import BaseModal from "../../../components/ui/BaseModal";
import InfoCard from "../../../components/ui/InfoCard";
import {
  AddressFields,
  ClientFormGrid,
  ClientPhoneField,
  ContactPersonField,
  EmailField,
  NameField,
  WebsiteField,
  useClientFormContext,
} from "../../Client/CreateEditClient/CreateEditClientForm";
import { getClientById, updateOwnCustomerProfile } from "../../../services/api/client";
import { me } from "../../../services/api/user";
import { showToast } from "../../../store/toast/toastSlice";
import {
  buildOwnCustomerFormData,
  formatPhoneNumber,
  getDefaultEntity,
  ownCustomerInitialValues,
  pickFormData,
  ownCustomerValidationSchema,
} from "../../../utils/helpers";

// The cards on the page, and the fields each one's modal edits and saves.
const SECTIONS = {
  logo: { title: "Company Logo", fields: ["clientLogo"] },
  information: {
    title: "Company Information",
    fields: [
      "clientName",
      "email",
      "phone",
      "manager",
      "address",
      "city",
      "state",
      "zipCode",
      "country",
      "website",
    ],
    Fields: ({ ctx }) => (
      <>
        <NameField ctx={ctx} />
        <EmailField ctx={ctx} />
        <ClientPhoneField ctx={ctx} />
        <ContactPersonField ctx={ctx} />
        <AddressFields ctx={ctx} />
        <WebsiteField ctx={ctx} />
      </>
    ),
  },
};

// The customer's own company, read as cards the way a client reads theirs in Company
// Settings. Editing a card opens the fields for that card only, drawn from the same form
// the complete-profile step uses, so they are defined in one place.
const CustomerCompanyDetails = () => {
  const dispatch = useDispatch();
  // The SECTIONS key whose modal is open.
  const [editingSection, setEditingSection] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);

  const { data: userData } = useQuery({ queryKey: ["userData"], queryFn: me, retry: 2 });
  const customerId = userData?.customer?.id;

  const {
    data: clientData,
    isPending: isClientLoading,
    refetch: refetchClient,
  } = useQuery({
    queryKey: ["client", String(customerId)],
    queryFn: () => getClientById(customerId),
    enabled: !!customerId,
  });

  const { isPending: isSaving, mutateAsync: saveProfile } = useMutation({
    mutationFn: updateOwnCustomerProfile,
  });

  const customer = clientData?.data;
  // The contact person belongs to the default entity, not the customer, so it is read from there.
  const entity = getDefaultEntity(customer);

  const handleSave = async (values) => {
    try {
      const formData = buildOwnCustomerFormData(values);
      await saveProfile(pickFormData(formData, SECTIONS[editingSection]?.fields));
      dispatch(showToast({ message: "Company updated successfully", type: "success" }));
      await refetchClient();
      setLogoFailed(false);
      setEditingSection(null);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to update company",
          type: "error",
        }),
      );
    }
  };

  const openFields = SECTIONS[editingSection]?.fields;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ownCustomerInitialValues(customer),
    validationSchema: openFields
      ? ownCustomerValidationSchema.pick(
          openFields.filter((field) => ownCustomerValidationSchema.fields[field]),
        )
      : undefined,
    onSubmit: handleSave,
  });

  const formCtx = useClientFormContext({
    formik,
    lockedFields: ["clientName", "phone", "email"],
  });

  if (!customerId || !customer) {
    return isClientLoading || !userData ? (
      <div className="flex justify-center py-10">
        <BaseLoader isLoading />
      </div>
    ) : (
      <p className="py-4">No customer record is linked to this account.</p>
    );
  }

  const editPencil = (section) => (
    <i
      className="fa-light fa-pencil text-gray-900 cursor-pointer"
      onClick={() => setEditingSection(section)}
    ></i>
  );

  const logoUrl = customer?.clientLogo?.url;

  const section = SECTIONS[editingSection];
  const closeModal = () => {
    formik.resetForm();
    setEditingSection(null);
  };

  // A newly picked file previews from the browser; a saved one from its stored link.
  const pickedLogo = formik.values.clientLogo;
  const logoPreview =
    pickedLogo instanceof File ? URL.createObjectURL(pickedLogo) : pickedLogo?.url || null;

  return (
    <div className="max-h-[calc(100vh-220px)] overflow-y-auto pb-4">
      <InfoCard title="Company Logo" actions={editPencil("logo")}>
        <div className="py-4">
          <div className="h-40 flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 rounded-md p-4">
            {logoUrl && !logoFailed ? (
              <img
                src={logoUrl}
                alt="Company Logo"
                className="max-h-32 object-contain rounded"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <>
                <i className="fa-regular fa-image text-gray-400 text-2xl"></i>
                <span className="text-sm text-gray-500">No logo uploaded</span>
              </>
            )}
          </div>
        </div>
      </InfoCard>

      <InfoCard
        title="Company Information"
        actions={editPencil("information")}
        items={[
          ["Company Name", customer?.clientName],
          ["Contact Person", entity?.managerName],
          ["Email", customer?.email],
          ["Phone", formatPhoneNumber(customer?.phoneNumber)],
          ["Website", customer?.website],
          ["Address", customer?.address?.addressLine1],
          ["City", customer?.address?.city],
          ["State", customer?.address?.state],
          ["Zip / Post Code", customer?.address?.zipCode],
          ["Country", customer?.address?.country],
        ]}
      />

      {section && (
        <BaseModal
          className="-top-[200px] overflow-auto"
          open
          close={closeModal}
          style={{ width: "1060px" }}
        >
          <div className="py-4 px-8">
            <div className="flex justify-between items-center border-b-2 pb-1">
              <h5 className="text-dark font-normal">{section.title}</h5>
            </div>
          </div>

          <div className="px-4 pb-4">
            {editingSection === "logo" ? (
              // The logo is uploaded into the same dashed frame it is read in.
              <div className="px-4 pt-4">
                <div
                  className="h-48 flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 rounded-md p-4 cursor-pointer bg-white hover:border-primary transition"
                  onClick={() => document.getElementById("customerLogoInput").click()}
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Company Logo"
                      className="max-h-32 object-contain rounded"
                    />
                  ) : (
                    <>
                      <i className="fa-solid fa-paperclip text-primary text-2xl"></i>
                      <span className="text-sm text-gray-500">Click or drop file here</span>
                      <span className="text-xs text-gray-400">Must be a JPG or PNG file</span>
                    </>
                  )}
                  <input
                    type="file"
                    id="customerLogoInput"
                    accept="image/png, image/jpeg, image/webp"
                    className="hidden"
                    onChange={(event) => {
                      const picked = event.target.files[0];
                      if (picked) formik.setFieldValue("clientLogo", picked);
                    }}
                  />
                </div>
              </div>
            ) : (
              <ClientFormGrid ctx={formCtx} compactTop>
                <section.Fields ctx={formCtx} />
              </ClientFormGrid>
            )}
          </div>

          <div
            className="d-flex content-center bg-white py-6"
            style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
          >
            <BaseButton
              variant="outlined"
              color="primary"
              className="px-[28px] py-[5px] bg-light text-primary hover:text-primary"
              style={{ marginRight: "16px" }}
              disabled={isSaving}
              onClick={closeModal}
            >
              Cancel
            </BaseButton>
            <BaseButton
              loaderSize="22px"
              isLoading={isSaving}
              color="primary"
              className="py-[5px] px-[38px]"
              disabled={isSaving || !formik.dirty}
              onClick={formik.handleSubmit}
            >
              Update
            </BaseButton>
          </div>
        </BaseModal>
      )}
    </div>
  );
};

export default CustomerCompanyDetails;
